import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getEventState, type EventPhase } from './api/event';

/**
 * The contest-end / Free Play week (GHS5-85). Season 5's five contest weeks are
 * followed by Week 6 = prize-free "Free Play": a real IN_EVENT week, so the game
 * stays playable and scores bucket to weekNumber=6. POST_EVENT remains the hard
 * shutdown. See .claude/STATUS.md.
 */
export const FREE_PLAY_WEEK = 6;

interface EventStateValue {
  phase: EventPhase | null;
  currentWeek: number | null;
  nextTransitionAt: string | null;
  /**
   * True once the site should present the contest-end face: hide the
   * Leaderboard/Rules nav, drop the chair + prize copy, switch the hero to
   * "Watch Season 5 Now". Driven by `currentWeek >= FREE_PLAY_WEEK` (Free Play
   * is live) OR `phase === POST_EVENT` (game shut down but chrome must stay
   * consistent). Defaults FALSE while loading and on error — we fail toward the
   * normal contest face so a slow/failed /event/state never hides live contest
   * elements. The only cost is a one-paint flash of contest chrome during the
   * post-contest window, never the reverse.
   */
  contestEnded: boolean;
  loading: boolean;
}

const DEFAULT: EventStateValue = {
  phase: null,
  currentWeek: null,
  nextTransitionAt: null,
  contestEnded: false,
  loading: true,
};

const EventStateContext = createContext<EventStateValue>(DEFAULT);

type Fetched = Pick<EventStateValue, 'phase' | 'currentWeek' | 'nextTransitionAt' | 'loading'>;

/**
 * Fetches /event/state and shares it. Consumers that only need the
 * contest-end/Free Play branch (AppBar, HeroSection, LandingStep) read
 * `contestEnded` from here instead of each firing their own fetch —
 * intentionally avoiding piling onto the /event/state request volume.
 *
 * Auto-refresh at transitions: a player sitting on the site must see the site
 * flip at a phase/week boundary WITHOUT a manual refresh. So after each fetch
 * we schedule a single re-fetch at `nextTransitionAt` (server-relative timing,
 * +buffer), which chains into the next boundary. We also re-fetch when the tab
 * returns to the foreground (background timers get throttled, so a transition
 * that fired while hidden is reconciled on wake). This is a SCHEDULED single
 * re-fetch per boundary, not a poll loop — deliberately kept off the
 * refetch-burst path.
 */
export function EventStateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Fetched>({
    phase: null,
    currentWeek: null,
    nextTransitionAt: null,
    loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let inFlight = false;

    // Cap on the scheduled delay: (1) dodges setTimeout's 32-bit overflow for a
    // far-future boundary (the open-ended Free Play → POST_EVENT shutoff sits a
    // year out until ops sets it), and (2) doubles as a slow re-sync so an idle
    // tab also picks up an ops SCHEDULE EDIT within this window (and instantly
    // on tab refocus / navigation). A 5s floor prevents a hot loop if the
    // server ever hands back an already-past boundary.
    const MAX_DELAY_MS = 6 * 60 * 60 * 1000;
    const MIN_DELAY_MS = 5_000;

    function scheduleNext(nextTransitionAt: string | null, serverTime: string): void {
      window.clearTimeout(timer);
      if (!nextTransitionAt) return; // POST_EVENT / no weeks — nothing more will fire
      const serverNow = new Date(serverTime).getTime();
      const target = new Date(nextTransitionAt).getTime();
      if (Number.isNaN(serverNow) || Number.isNaN(target)) return;
      // Delay measured server-relative so client clock skew can't fire it early;
      // +3s lands us just PAST the boundary (the API derives phase from live
      // time, flipping exactly at nextTransitionAt).
      const raw = target - serverNow + 3_000;
      const delay = Math.min(MAX_DELAY_MS, Math.max(MIN_DELAY_MS, raw));
      timer = window.setTimeout(load, delay);
    }

    function load(): void {
      if (cancelled || inFlight) return;
      inFlight = true;
      getEventState()
        .then((data) => {
          if (cancelled) return;
          setState({
            phase: data.phase,
            currentWeek: data.currentWeek,
            nextTransitionAt: data.nextTransitionAt,
            loading: false,
          });
          scheduleNext(data.nextTransitionAt, data.serverTime);
        })
        .catch(() => {
          if (cancelled) return;
          // Fail toward the normal contest face (contestEnded stays false), and
          // arm a bounded retry so one transient failure doesn't break the chain.
          setState((s) => ({ ...s, loading: false }));
          window.clearTimeout(timer);
          timer = window.setTimeout(load, 60_000);
        })
        .finally(() => {
          inFlight = false;
        });
    }

    function onVisible(): void {
      if (document.visibilityState === 'visible') load();
    }

    document.addEventListener('visibilitychange', onVisible);
    load();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const contestEnded =
    state.phase === 'POST_EVENT' ||
    (state.currentWeek !== null && state.currentWeek >= FREE_PLAY_WEEK);

  return (
    <EventStateContext.Provider value={{ ...state, contestEnded }}>
      {children}
    </EventStateContext.Provider>
  );
}

export function useEventState(): EventStateValue {
  return useContext(EventStateContext);
}
